import {
    WaitlistFormAddonOption,
    WaitlistFormClientPet,
    WaitlistFormServiceOption
} from 'actions/waitlists/WaitlistFormApi';
import { faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddOnService, { createEmptyAddOnService } from '@spike/addon-service-model';
import { MarketplaceSchedule } from '@spike/model';
import Pet from '@spike/pet-model';
import clsx from 'clsx';
import SelectAddOn from 'components/CreateBooking/AddOnSelector/SelectAddOn';
import ServiceTypeTags from 'components/CreateBooking/Daycare/UI/ServiceTypeTags';
import SelectPets from 'components/CreateBooking/PetSearch/SelectPets';
import Button from 'components/UI/V3/Button/Button';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { Select } from 'components/UI/V3/Forms/Select/Select';
import { PetProfileCard } from 'components/UI/V3/PetProfileCard/PetProfileCard';
import Spinner from 'components/UI/V3/Spinner';
import {
    getAddonOptionsForService,
    WaitlistFormPet,
    WaitlistFormService
} from 'components/Waitlist/WaitlistForm/waitlistFormUtils';
import { Staff } from 'model/Staff';
import { WAITLIST_SERVICE_KEYS, WaitlistServiceKey } from 'model/Waitlist';
import { ComponentProps, FunctionComponent, useEffect, useMemo, useState } from 'react';
import styles from './WaitlistPetServicePicker.module.scss';
import WaitlistServiceDatesField from './WaitlistServiceDatesField';
import WaitlistServiceSelect from './WaitlistServiceSelect';

export interface WaitlistPetServicePickerResult {
    pet: WaitlistFormClientPet;
    service: WaitlistFormService;
    /** Service replaced in the pet (add-ons mode lets the staff pick which one). */
    serviceIndex?: number;
}

interface WaitlistPetServicePickerProps {
    /** Pets the staff can pick. */
    pets: Array<WaitlistFormClientPet>;
    loadingPets?: boolean;
    profiles: Array<Pet>;
    initialPetId?: number;
    /** The pet can't be changed (adding or editing a service of a pet already in the waitlist). */
    lockPet?: boolean;
    /** Add-ons for one of the services the pet already has; no new service. */
    addonsOnly?: boolean;
    /** Service being edited. */
    initialService?: WaitlistFormService;
    /** Services already in the waitlist for each pet, so they are not offered twice. */
    currentPets: Array<WaitlistFormPet>;
    petServices: Record<number, Array<WaitlistFormServiceOption>>;
    addonOptions: Array<WaitlistFormAddonOption>;
    /** Staff of the waitlist location; empty when it is not the current location. */
    staff: Array<Staff>;
    timeZone: string;
    schedule?: MarketplaceSchedule;
    onLoadPetServices: (petId: number) => void;
    /** "Add Pet", for a client without pets. */
    onAddPet?: () => void;
    onBack: () => void;
    onSave: (result: WaitlistPetServicePickerResult) => void;
}

type ServiceType = ComponentProps<typeof ServiceTypeTags>['selectedServiceType'];

// Dental services are under the Grooming tag, as in Add Appointment.
const getServiceType = (businessArea: string) => (businessArea === 'dental' ? 'grooming' : businessArea);

const getAreaOrder = (area: string) => {
    const index = WAITLIST_SERVICE_KEYS.indexOf(area as WaitlistServiceKey);
    return index === -1 ? WAITLIST_SERVICE_KEYS.length : index;
};

// The add-on selector of Add Appointment takes catalog add-ons.
const toAddOnService = (addon: WaitlistFormAddonOption): AddOnService => ({
    ...createEmptyAddOnService(() => String(addon.id)),
    id: addon.id,
    name: addon.name,
    price: addon.price ?? 0,
    businessAreas: addon.businessAreas
});

export const WaitlistPetServicePicker: FunctionComponent<WaitlistPetServicePickerProps> = ({
    pets,
    loadingPets,
    profiles,
    initialPetId,
    lockPet,
    addonsOnly,
    initialService,
    currentPets,
    petServices,
    addonOptions,
    staff,
    timeZone,
    schedule,
    onLoadPetServices,
    onAddPet,
    onBack,
    onSave
}) => {
    const [petId, setPetId] = useState<number | undefined>(
        initialPetId ?? (pets.length === 1 ? pets[0].id : undefined)
    );
    const [area, setArea] = useState<string | undefined>(
        initialService ? getServiceType(initialService.businessArea) : undefined
    );
    const [serviceId, setServiceId] = useState<number | undefined>(initialService?.serviceId);
    const [addonIds, setAddonIds] = useState<Array<number>>(
        initialService?.addons.map(addon => addon.addonServiceId) ?? []
    );
    // Each service has its own preferred staff; a new service starts with No preference.
    const [staffId, setStaffId] = useState<number | null>(initialService?.preferredStaffId ?? null);
    // Each service has its own dates ([] = ASAP).
    const [dates, setDates] = useState<Array<string>>(initialService?.dates ?? []);

    const petServicesInWaitlist = currentPets.find(current => current.id === petId)?.services ?? [];
    const [addonServiceIndex, setAddonServiceIndex] = useState(0);

    const pet = pets.find(current => current.id === petId);
    const profile = profiles.find(current => current.id === petId);
    const services = petId !== undefined ? petServices[petId] : undefined;

    // The pets load after the step opens: a single pet is preselected once they arrive.
    useEffect(() => {
        petId === undefined && pets.length === 1 && setPetId(pets[0].id);
    }, [pets.length]);

    useEffect(() => {
        petId !== undefined && services === undefined && onLoadPetServices(petId);
    }, [petId]);

    const takenServiceIds = (currentPets.find(current => current.id === petId)?.services ?? [])
        .map(service => service.serviceId)
        .filter(id => id !== initialService?.serviceId);

    const availableServices = (services ?? []).filter(service => !takenServiceIds.includes(service.id));
    // In the order of the tags, so the first one is selected by default.
    const areas = Array.from(new Set(availableServices.map(service => getServiceType(service.businessArea)))).sort(
        (area, other) => getAreaOrder(area) - getAreaOrder(other)
    );

    // Default to the first area the pet has services for.
    useEffect(() => {
        if (areas.length > 0 && (area === undefined || !areas.includes(area))) {
            setArea(areas[0]);
        }
    }, [areas.join(',')]);

    const areaServices = availableServices.filter(service => getServiceType(service.businessArea) === area);
    const waitlistService = addonsOnly ? petServicesInWaitlist[addonServiceIndex] : undefined;
    const selectedService = waitlistService
        ? {
              id: waitlistService.serviceId,
              name: waitlistService.name,
              businessArea: waitlistService.businessArea,
              price: waitlistService.price
          }
        : availableServices.find(service => service.id === serviceId);

    const serviceAddons = useMemo(
        () =>
            selectedService
                ? getAddonOptionsForService(
                      {
                          serviceId: selectedService.id,
                          name: selectedService.name,
                          businessArea: selectedService.businessArea,
                          price: selectedService.price,
                          dates: [],
                          preferredStaffId: null,
                          addons: []
                      },
                      addonOptions
                  )
                : [],
        [selectedService, addonOptions]
    );

    const addonServiceOptions = useMemo(
        () => serviceAddons.filter(addon => !addonIds.includes(addon.id)).map(toAddOnService),
        [serviceAddons, addonIds]
    );

    // Pet profiles (type for the chip) in the order of the selectable pets.
    const petOptions = pets.flatMap(current => profiles.find(candidate => candidate.id === current.id) ?? []);

    const staffOptions = staff.filter(member => member.services.some(service => service.id === serviceId));

    const changePetHandler = (value: number) => {
        setPetId(value);
        setServiceId(undefined);
        setAddonIds([]);
    };

    // The dates mean something different in each area (days, nights), so they start over.
    const changeAreaHandler = (value: string) => {
        setArea(value);
        setServiceId(undefined);
        setAddonIds([]);
        setDates([]);
    };

    const changeServiceHandler = (value: number) => {
        setServiceId(value);
        setAddonIds([]);
        // Keep the staff only if they also offer the new service (other
        // locations have no staff list to check, so it stays).
        setStaffId(current =>
            staff.length === 0 ||
            staff.some(member => member.id === current && member.services.some(service => service.id === value))
                ? current
                : null
        );
    };

    const removeAddon = (id: number) => setAddonIds(current => current.filter(addonId => addonId !== id));

    const saveHandler = () => {
        if (!pet || !selectedService) {
            return;
        }

        // Editing keeps the waitlist_service id (also when the service changes, so the same item is updated).
        // The same service keeps its add-ons and price snapshot; a different one takes the current price.
        const baseService = waitlistService ?? initialService;
        const sameService = baseService?.serviceId === selectedService.id;

        onSave({
            pet,
            serviceIndex: addonsOnly ? addonServiceIndex : undefined,
            service: {
                id: baseService?.id,
                serviceId: selectedService.id,
                name: selectedService.name,
                businessArea: selectedService.businessArea,
                price: sameService && baseService ? baseService.price : selectedService.price,
                // Adding add-ons doesn't show the dates: the service keeps them.
                dates: waitlistService ? waitlistService.dates : dates,
                // Adding add-ons doesn't show the staff selector: the service keeps its staff.
                preferredStaffId: waitlistService ? waitlistService.preferredStaffId : staffId,
                preferredStaffName: waitlistService?.preferredStaffName ?? initialService?.preferredStaffName,
                addons: addonIds.map(addonId => {
                    const existing = sameService
                        ? baseService?.addons.find(addon => addon.addonServiceId === addonId)
                        : undefined;
                    const option = addonOptions.find(addon => addon.id === addonId);

                    return (
                        existing ?? { addonServiceId: addonId, name: option?.name ?? '', price: option?.price ?? null }
                    );
                })
            }
        });
    };

    return (
        <>
            <DrawerHeader
                title="Pets & Services"
                onBack={onBack}
            />

            <DrawerBody className={styles.body}>
                <div className={clsx(styles.section, { [styles.locked]: lockPet })}>
                    <Label>Pets</Label>
                    {loadingPets ? (
                        <Spinner size={24} />
                    ) : onAddPet ? (
                        <>
                            <p className={styles.muted}>This client has no pets.</p>
                            <button
                                type="button"
                                className={styles.addPet}
                                onClick={onAddPet}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Add Pet
                            </button>
                        </>
                    ) : (
                        <SelectPets
                            placeholder="Select a Pet"
                            selectedOption={petId !== undefined ? String(petId) : undefined}
                            options={petOptions}
                            onSelect={selected => changePetHandler(selected.id!)}
                        />
                    )}
                </div>

                {profile && <PetProfileCard pet={profile} />}

                {pet && (
                    <>
                        <hr className={styles.divider} />

                        {!addonsOnly && services === undefined && <Spinner size={24} />}

                        {!addonsOnly && services !== undefined && availableServices.length === 0 && (
                            <p className={styles.muted}>No more services available for {pet.name}.</p>
                        )}

                        {addonsOnly && (
                            <Select
                                label="Service"
                                value={String(addonServiceIndex)}
                                options={petServicesInWaitlist.map((service, index) => ({
                                    label: service.name,
                                    value: String(index)
                                }))}
                                onChange={value => {
                                    setAddonServiceIndex(Number(value));
                                    setAddonIds(
                                        petServicesInWaitlist[Number(value)].addons.map(addon => addon.addonServiceId)
                                    );
                                }}
                            />
                        )}

                        {!addonsOnly && areas.length > 0 && (
                            <ServiceTypeTags
                                hideLabel
                                showMembershipsTag={false}
                                className={styles.areas}
                                serviceTypes={areas}
                                selectedServiceType={area as ServiceType}
                                onSelectServiceType={serviceType => serviceType && changeAreaHandler(serviceType)}
                            />
                        )}

                        {!addonsOnly && areaServices.length > 0 && (
                            <WaitlistServiceSelect
                                services={areaServices}
                                value={serviceId}
                                onChange={changeServiceHandler}
                            />
                        )}

                        {serviceAddons.length > 0 && (
                            <div className={styles.section}>
                                <Label>Add-ons</Label>
                                {addonServiceOptions.length > 0 && (
                                    <SelectAddOn
                                        placeholder="Select an Add-On"
                                        options={addonServiceOptions}
                                        onSelect={addon =>
                                            addon.id !== undefined && setAddonIds(current => [...current, addon.id!])
                                        }
                                    />
                                )}
                                {addonIds.length > 0 && (
                                    <ul className={styles.addons}>
                                        {addonIds.map(addonId => {
                                            const addon = serviceAddons.find(current => current.id === addonId);

                                            return (
                                                addon && (
                                                    <li key={addonId}>
                                                        <span>{addon.name}</span>
                                                        <button
                                                            type="button"
                                                            aria-label={`Remove ${addon.name}`}
                                                            onClick={() => removeAddon(addonId)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrashCan} />
                                                        </button>
                                                    </li>
                                                )
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}

                        {!addonsOnly && selectedService && staffOptions.length > 0 && (
                            <Select<Staff | undefined>
                                label="Preferred Staff"
                                value={staffId !== null ? String(staffId) : 'none'}
                                options={[
                                    { label: 'No preference', value: 'none', data: undefined },
                                    ...staffOptions.map(member => ({
                                        label: `${member.person.firstName} ${member.person.lastName}`,
                                        value: String(member.id),
                                        data: member
                                    }))
                                ]}
                                renderOption={option => (
                                    <span className={styles.staffOption}>
                                        {option.data?.person.avatar && (
                                            <img
                                                src={option.data.person.avatar}
                                                alt=""
                                            />
                                        )}
                                        {option.label}
                                    </span>
                                )}
                                onChange={value => setStaffId(value === 'none' ? null : Number(value))}
                            />
                        )}

                        {!addonsOnly && selectedService && (
                            <WaitlistServiceDatesField
                                key={selectedService.businessArea}
                                businessArea={selectedService.businessArea}
                                dates={dates}
                                timeZone={timeZone}
                                schedule={schedule}
                                onChange={setDates}
                            />
                        )}
                    </>
                )}
            </DrawerBody>

            <DrawerFooter>
                <Button
                    fullWidth
                    size="large"
                    variant="green"
                    label="Save"
                    disabled={!pet || !selectedService}
                    onClick={saveHandler}
                />
            </DrawerFooter>
        </>
    );
};

export default WaitlistPetServicePicker;
